import React from 'react'
import cx from 'classnames'
import zip from 'lodash/zip'
import flatten from 'lodash/flatten'
import RA from 'ra-config'
import I18n from 'lib/components/i18n'
import comparisonGroups from './comparison-groups'
import InfoLabel from '../../info-label'
import { labelForStrategy } from '../../../attributes'
import { descriptionForStrategy } from '../../../config/description'
import * as placeholder from 'lib/config/placeholder'
import { getShades } from '../../../config/colors'
import { formatTable } from '../../../format'

const TOOLTIPS = RA.content.strategies.attributeInfoText
const TABLE_BODY = '.performance-table__body'

function Headings({ cells, source }) {
  const shades = getShades(cells, 'Name')

  const children = cells.map(({ Name }) => {
    const style = { borderBottom: '4px solid ' + shades[Name] }
    return (
      <th key={Name} style={style} className="performance-table__header">
        {Name}
      </th>
    )
  })

  const sourceText = source ? placeholder.replace('{sourceText}') : ''

  return (
    <thead>
      <tr>
        <th className="source-text">
          <span hidden>Strategy</span>
          <span>{sourceText}</span>
        </th>
        {children}
      </tr>
    </thead>
  )
}

function SingleRow({ definition, cells }) {
  const { attribute } = definition
  const label = labelForStrategy(attribute)

  return (
    <tr key={attribute}>
      <td key="label" className="performance-table__row-label">
        <InfoLabel
          overlay={TOOLTIPS[attribute]}
          label={label}
          scrollParent={TABLE_BODY}
        />
      </td>
      {cells.map(strategy => {
        const value = strategy[attribute]

        return (
          <td key={attribute + strategy.ID}>{formatTable(value, attribute)}</td>
        )
      })}
    </tr>
  )
}

function SplitRow({ definition, cells }) {
  const { labelAttribute, attributePrefix, attributeSuffixes } = definition
  const label = labelForStrategy(labelAttribute)

  return (
    <tr className="performance-table__row-split">
      <td className="performance-table__row-label">
        <InfoLabel
          overlay={TOOLTIPS[labelAttribute]}
          label={label}
          scrollParent={TABLE_BODY}
        />
      </td>
      {cells.map(strategy => {
        const values = attributeSuffixes.map(suffix => {
          let attribute = attributePrefix + suffix
          return [attribute, strategy[attribute]]
        })

        return (
          <td
            key={attributePrefix + strategy.ID}
            className="performance-table__row-split__cell"
          >
            <table>
              <tbody>
                <tr>
                  {values.map(([attribute, value]) => {
                    return (
                      <td key={attribute}>{formatTable(value, attribute)}</td>
                    )
                  })}
                </tr>
              </tbody>
            </table>
          </td>
        )
      })}
    </tr>
  )
}

function TableRow({ definition, cells }) {
  const { labelAttribute, headings, attributes } = definition
  const label = labelForStrategy(labelAttribute)

  return (
    <tr>
      <td className="performance-table__row-label">{label}</td>
      {cells.map(strategy => {
        const values = attributes.map(attribute => [
          attribute,
          strategy[attribute]
        ])

        return (
          <td
            key={strategy.ID}
            className="performance-table__header-nested--condensed"
          >
            <table className="performance-table__nested-table">
              <thead>
                <tr>
                  {headings.map((heading, i) => (
                    <th key={i} dangerouslySetInnerHTML={{ __html: heading }} />
                  ))}
                </tr>
              </thead>
              <tbody>
                <tr>
                  {values.map(([attribute, value], i) => {
                    return <td key={i}>{formatTable(value, attribute)}</td>
                  })}
                </tr>
              </tbody>
            </table>
          </td>
        )
      })}
    </tr>
  )
}

function Row(props) {
  if (props.definition.type === 'single') return <SingleRow {...props} />
  if (props.definition.type === 'split') return <SplitRow {...props} />
  if (props.definition.type === 'table') return <TableRow {...props} />
}

function WysiwygCell({ text, classes }) {
  return (
    <td className={classes}>
      <span
        className="wysiwyg"
        dangerouslySetInnerHTML={{ __html: text || 'No description' }}
      />
    </td>
  )
}

function Description({ cells, region }) {
  return (
    <tbody>
      <tr className="performance-table__row-align-top">
        <td className="performance-table__row-label">Description</td>
        {cells.map(strategy => {
          const text = descriptionForStrategy(
            strategy.Name,
            strategy.Category,
            region.value
          )
          return (
            <WysiwygCell
              key={strategy.Name}
              text={text}
              classes="performance-table__description"
            />
          )
        })}
      </tr>
    </tbody>
  )
}

function StatsGroup({ group, cells }) {
  const { label, showInnerColumnHeadings, attributes } = group
  const tableClass = cx('table-body-bordered-bottom', {
    'table-body-empty': !showInnerColumnHeadings
  })

  return (
    <tbody>
      <tr className="performance-table__row-split performance-table__row-split--condensed">
        <I18n tag="td" className="performance-table__row-group-label">
          {label}
        </I18n>
        {cells.map((_, i) => {
          return (
            <th key={i}>
              <table className={tableClass}>
                <tbody>
                  <tr className="performance-table__inner-column-headings">
                    <th>{showInnerColumnHeadings && 'Net of Trading Cost'}</th>
                    <th>
                      {showInnerColumnHeadings && 'Gross of Trading Cost'}
                    </th>
                  </tr>
                </tbody>
              </table>
            </th>
          )
        })}
      </tr>
      {attributes.map((definition, i) => {
        return <Row key={label + i} definition={definition} cells={cells} />
      })}
    </tbody>
  )
}

export default function HistoricalTable({ cells = [], region }) {
  const groups = comparisonGroups.map(group => {
    return <StatsGroup key={group.label} group={group} cells={cells} />
  })

  const headings = new Array(comparisonGroups.length - 1)
    .fill(null)
    .map((_, i) => <Headings key={i} cells={cells} />)

  const components = flatten(zip(groups, headings))

  return (
    <table>
      <Headings cells={cells} source={true} />
      <Description cells={cells} region={region} />
      {components}
    </table>
  )
}



// WEBPACK FOOTER //
// ./src/components/strategies/historical/table.js